import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import client from '../api/client';
import { useSettings } from '../context/SettingsContext';
import { X, Check, User, Package, Briefcase } from 'lucide-react';

const TransactionModal = ({ 
    isOpen, onClose, onSuccess, initialData, 
    wallets, categories, projects, clients, services 
}) => {
    const queryClient = useQueryClient();
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [formData, setFormData] = useState({
        date: new Date().toISOString().split('T')[0],
        type: 'Расход',
        amount: '',
        wallet_id: '',
        category: '',
        notes: '',
        project_id: '',
        client_id: '',
        product_id: ''
    });

    const { currency } = useSettings();

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (initialData) {
            setFormData({
                ...initialData,
                date: new Date(initialData.date).toISOString().split('T')[0],
                wallet_id: initialData.wallet_id || '',
                project_id: initialData.project_id || '',
                client_id: initialData.client_id || '',
                product_id: initialData.product_id || ''
            });
        } else {
            setFormData({
                date: new Date().toISOString().split('T')[0],
                type: 'Расход',
                amount: '',
                wallet_id: '',
                category: '',
                notes: '',
                project_id: '',
                client_id: '',
                product_id: ''
            });
        }
    }, [initialData, isOpen]);

    const mutation = useMutation({
        mutationFn: (data) => {
            if (initialData) {
                return client.put(`/transactions/${initialData.id}`, data);
            }
            return client.post('/transactions', data);
        },
        onSuccess: () => {
            onSuccess();
        },
        onError: (error) => {
            const msg = error.response?.data?.detail || error.message;
            alert('Ошибка при сохранении: ' + msg);
        }
    });

    if (!isOpen) return null;

    const handleProductChange = (pid) => {
        const selected = services?.find(s => s.id === parseInt(pid));
        const updates = { product_id: pid ? parseInt(pid) : '' };
        
        if (selected?.price > 0 && !formData.amount) {
            updates.amount = selected.price;
        }
        
        setFormData({ ...formData, ...updates });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const payload = {
            ...formData,
            amount: parseFloat(formData.amount) || 0,
            wallet_id: formData.wallet_id ? parseInt(formData.wallet_id) : null,
            project_id: formData.project_id === 'general' ? 'general' : (formData.project_id ? parseInt(formData.project_id) : null),
            client_id: formData.client_id ? parseInt(formData.client_id) : null,
            product_id: formData.product_id ? parseInt(formData.product_id) : null
        };
        mutation.mutate(payload);
    };

    return (
        <div className={`fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex ${isMobile ? 'items-end' : 'items-center justify-center'} p-0 md:p-4`}>
            <div
                className={`bg-neutral-900 border-x border-t md:border border-neutral-800 w-full max-w-xl 
                    ${isMobile ? 'rounded-t-[2.5rem] animate-in slide-in-from-bottom duration-300' : 'rounded-3xl animate-in zoom-in-95 duration-200'} 
                    shadow-2xl overflow-hidden max-h-[92dvh] flex flex-col`}
            >
                {isMobile && (
                    <div className="w-full flex justify-center pt-3 shrink-0">
                        <div className="w-12 h-1 bg-neutral-800 rounded-full" />
                    </div>
                )}

                <div className="p-6 md:p-8 overflow-y-auto custom-scrollbar">
                    <div className="flex justify-between items-center mb-6 md:mb-8 sticky top-0 bg-neutral-900/95 backdrop-blur z-10 py-2 -mt-2">
                        <h3 className="text-xl font-black text-white uppercase tracking-tight italic">
                            {initialData ? 'Редактировать' : 'Новая операция'}
                        </h3>
                        <button onClick={onClose} className="p-2 bg-neutral-800/50 hover:bg-neutral-800 rounded-full transition-all text-neutral-500 hover:text-white">
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6 pb-10 md:pb-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest ml-1">Тип операции</label>
                                <div className="flex bg-neutral-800 p-1.5 rounded-[2rem] border border-neutral-700/50">
                                    <button
                                        type="button"
                                        onClick={() => setFormData({ ...formData, type: 'Расход' })}
                                        className={`flex-1 py-3.5 rounded-[1.5rem] font-black uppercase text-[10px] tracking-widest transition-all ${formData.type === 'Расход' ? 'bg-rose-600 text-white shadow-lg shadow-rose-600/20' : 'text-neutral-500 hover:text-neutral-300'}`}
                                    >
                                        Расход
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setFormData({ ...formData, type: 'Доход' })}
                                        className={`flex-1 py-3.5 rounded-[1.5rem] font-black uppercase text-[10px] tracking-widest transition-all ${formData.type === 'Доход' ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-600/20' : 'text-neutral-500 hover:text-neutral-300'}`}
                                    >
                                        Доход
                                    </button>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest ml-1">Дата</label>
                                <input
                                    type="date"
                                    required
                                    value={formData.date}
                                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                                    className="w-full bg-neutral-800 border-2 border-neutral-800 text-white p-4 rounded-3xl outline-none focus:border-blue-500/50 transition-all font-bold"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest ml-1">Сумма</label>
                            <div className="relative">
                                <input
                                    type="number"
                                    required
                                    value={formData.amount}
                                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                                    className="w-full bg-neutral-800/50 border-2 border-neutral-800 text-white p-5 rounded-[2rem] outline-none focus:border-emerald-500 transition-all font-black text-3xl placeholder:text-neutral-800"
                                    placeholder="0"
                                />
                                <span className="absolute right-6 top-1/2 -translate-y-1/2 text-xl font-black text-neutral-600">{currency}</span>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest ml-1 flex items-center gap-1.5"><User size={10} /> Участник Клуба</label>
                                <select
                                    value={formData.client_id}
                                    onChange={(e) => setFormData({ ...formData, client_id: e.target.value ? parseInt(e.target.value) : '' })}
                                    className="w-full bg-neutral-800 border-2 border-neutral-800 text-white p-4 rounded-3xl outline-none focus:border-blue-500/50 transition-all font-bold appearance-none"
                                >
                                    <option value="">Не привязано</option>
                                    {clients?.map(c => <option key={c.id} value={c.id}>{c.full_name} {c.company_name ? `(${c.company_name})` : ''}</option>)}
                                </select>
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest ml-1 flex items-center gap-1.5"><Package size={10} /> Продукт / Событие</label>
                                <select
                                    value={formData.product_id}
                                    onChange={(e) => handleProductChange(e.target.value)}
                                    className="w-full bg-neutral-800 border-2 border-neutral-800 text-white p-4 rounded-3xl outline-none focus:border-blue-500/50 transition-all font-bold appearance-none"
                                >
                                    <option value="">Не привязано</option>
                                    {services?.filter(s => !s.parent_id).map(parent => (
                                        <optgroup key={parent.id} label={parent.name} className="bg-neutral-900">
                                            <option value={parent.id}>{parent.name} (Основной)</option>
                                            {services?.filter(s => s.parent_id === parent.id).map(child => (
                                                <option key={child.id} value={child.id}>↳ {child.name}</option>
                                            ))}
                                        </optgroup>
                                    ))}
                                    {/* Orphan services */}
                                    {services?.filter(s => !s.parent_id && !services.some(c => c.parent_id === s.id)).map(s => (
                                        <option key={s.id} value={s.id}>{s.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest ml-1">Кошелек</label>
                                <select
                                    required
                                    value={formData.wallet_id}
                                    onChange={(e) => setFormData({ ...formData, wallet_id: e.target.value })}
                                    className="w-full bg-neutral-800 border-2 border-neutral-800 text-white p-4 rounded-3xl outline-none focus:border-blue-500/50 transition-all font-bold appearance-none"
                                >
                                    <option value="">Выбрать...</option>
                                    {wallets?.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                                </select>
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest ml-1">Категория</label>
                                <select
                                    required
                                    value={formData.category}
                                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                    className="w-full bg-neutral-800 border-2 border-neutral-800 text-white p-4 rounded-3xl outline-none focus:border-blue-500/50 transition-all font-bold appearance-none"
                                >
                                    <option value="">Выбрать...</option>
                                    {categories?.filter(c => c.type === (formData.type === 'Доход' ? 'income' : 'expense')).map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                                </select>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest ml-1 flex items-center gap-1.5"><Briefcase size={10} /> Проект</label>
                            <select
                                value={formData.project_id}
                                onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
                                className="w-full bg-neutral-800 border-2 border-neutral-800 text-white p-4 rounded-3xl outline-none focus:border-blue-500/50 transition-all font-bold appearance-none"
                            >
                                <option value="">Без проекта</option>
                                <option value="general" className="text-amber-500 font-bold">--- ОБЩИЙ РАСХОД/ДОХОД ---</option>
                                {projects?.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                            </select>
                        </div>

                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest ml-1">Заметки</label>
                            <textarea
                                value={formData.notes}
                                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                className="w-full bg-neutral-800/50 border-2 border-neutral-800 text-white p-5 rounded-[2rem] outline-none focus:border-blue-500/50 transition-all font-medium h-28 resize-none text-sm"
                                placeholder="Опишите операцию..."
                            />
                        </div>

                        <div className={`flex gap-3 pt-4 ${isMobile ? 'sticky bottom-0 bg-neutral-900/95 backdrop-blur-xl py-4 pb-8' : ''}`}>
                            <button
                                type="button"
                                onClick={onClose}
                                className="flex-1 bg-neutral-800 hover:bg-neutral-700 text-neutral-500 font-black uppercase text-[10px] tracking-widest py-5 rounded-3xl transition-all"
                            >
                                Отмена
                            </button>
                            <button
                                type="submit"
                                disabled={mutation.isPending}
                                className="flex-[2] bg-blue-600 hover:bg-blue-500 text-white font-black uppercase text-[10px] tracking-widest py-5 rounded-3xl transition-all shadow-xl shadow-blue-600/20 disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {mutation.isPending ? 'Записываю...' : (
                                    <>
                                        <Check className="w-4 h-4" />
                                        {initialData ? 'Сохранить' : 'Готово'}
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default TransactionModal;
